import * as THREE from 'three';
import { _n, sprintf } from '@wordpress/i18n';
import {
	CONTENT_TOC_LINK_CLASSES,
	CONTENT_TOC_LIST_ITEM_CLASSES,
	initH2ContentToc,
	MOBILE_TOC_LINK_CLASSES,
	MOBILE_TOC_LIST_ITEM_CLASSES,
} from '../utils/content-toc';

const BLOCK_SELECTOR = '.wp-block-fc-block-fc-knowledge-hub-container';
const ACTIVE_CLASS = 'font-semibold';
const DEFAULT_VISIBLE_ITEMS = 5;
const IGNORED_HEADING_ANCESTOR_CLASSES = ['fc-knowledge-hub-links'];

function syncSectionCount(block, count) {
	const sectionCount = block.querySelector('[data-kh-section-count]');

	if (!sectionCount) {
		return;
	}

	sectionCount.textContent = sprintf(
		_n('%d section', '%d sections', count, 'fc-knowledge-hub-container'),
		count
	);
}

function initThreeGradient(target) {
	if (!target) return;

	const container =
		target.classList?.contains('fc-khc__bg-gradient') ||
		target.classList?.contains('fc-animated-gradient-bg')
			? target
			: target.querySelector?.(
					'.fc-khc__bg-gradient, .fc-animated-gradient-bg'
				);
	if (!container) return;

	if (
		container.dataset.fcGradientInit === '1' ||
		container.querySelector('canvas') ||
		(document.querySelector('.fc-animated-gradient-bg canvas') &&
			!container.classList.contains('fc-animated-gradient-bg'))
	) {
		return;
	}
	container.dataset.fcGradientInit = '1';

	const canvas = document.createElement('canvas');
	container.appendChild(canvas);

	const scene = new THREE.Scene();
	const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
	const renderer = new THREE.WebGLRenderer({
		canvas,
		antialias: true,
		alpha: true,
	});

	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

	const colorBlue = new THREE.Color('#2751D9');
	const colorIndigo = new THREE.Color('#173dbb');
	const colorViolet = new THREE.Color('#6d2bbd');
	const colorMagenta = new THREE.Color('#c016c9');

	const geometry = new THREE.PlaneGeometry(2, 2);
	const material = new THREE.ShaderMaterial({
		uniforms: {
			u_time: { value: 0 },
			u_colorBlue: { value: colorBlue },
			u_colorIndigo: { value: colorIndigo },
			u_colorViolet: { value: colorViolet },
			u_colorMagenta: { value: colorMagenta },
			u_aspect: { value: 1.0 },
		},
		vertexShader: `
			varying vec2 vUv;
			void main() {
				vUv = uv;
				gl_Position = vec4(position, 1.0);
			}
		`,
		fragmentShader: `
			uniform float u_time;
			uniform vec3 u_colorBlue;
			uniform vec3 u_colorIndigo;
			uniform vec3 u_colorViolet;
			uniform vec3 u_colorMagenta;
			uniform float u_aspect;
			varying vec2 vUv;

			float ribbon(float x, float centre, float width) {
				float distanceFromCentre = abs(x - centre);
				return 1.0 - smoothstep(0.0, width, distanceFromCentre);
			}

			vec3 getAuroraColour(float index) {
				if (index < 0.5) return u_colorViolet;
				if (index < 1.5) return u_colorIndigo;
				if (index < 2.5) return u_colorViolet;
				return u_colorMagenta;
			}

			void main() {
				// Several wide, feathered curtains feel more like an aurora than a
				// single colour boundary. Movement is deliberately slow and slight.
				float aspectCompensation = mix(
					0.82,
					1.0,
					clamp(u_aspect / 1.8, 0.0, 1.0)
				);
				float slowTime = u_time * 0.055;
				float y = vUv.y;
				float mobileAmount = 1.0 - smoothstep(0.75, 1.1, u_aspect);
				float mobileWidthScale = mix(1.0, 1.38, mobileAmount);
				float mobileOffset = 0.15 * mobileAmount;
				float broadWave =
					sin((y * 5.2) + slowTime) * 0.055 +
					sin((y * 10.5) - (slowTime * 0.7)) * 0.018;
				float fineWave = sin((y * 16.0) + (slowTime * 1.3)) * 0.012;
				float mobileWave = mobileAmount * (
					sin((y * 8.5) + (slowTime * 0.85)) * 0.11 +
					sin((y * 18.0) - (slowTime * 1.2)) * 0.03
				);
				float mobileWidthVariation = 1.0 + (
					mobileAmount * sin((y * 7.0) - (slowTime * 0.6)) * 0.18
				);

				float mainCentre = 0.76 + broadWave + mobileWave + mobileOffset;
				float secondCentre = 0.98 - (broadWave * 0.65) + fineWave - (mobileWave * 0.55) + mobileOffset;
				float thirdCentre = 0.54 + (broadWave * 0.45) - fineWave + (mobileWave * 0.75) + mobileOffset;

				float ribbonScale = aspectCompensation * mobileWidthScale;
				float haze = ribbon(vUv.x, mainCentre, 0.48 * ribbonScale);
				float violetCurtain = ribbon(
					vUv.x,
					mainCentre,
					0.25 * ribbonScale * mobileWidthVariation
				);
				float magentaCurtain = ribbon(
					vUv.x,
					secondCentre,
					0.26 * ribbonScale * (2.0 - mobileWidthVariation)
				);
				float innerGlow = ribbon(
					vUv.x,
					thirdCentre,
					0.16 * ribbonScale * mobileWidthVariation
				);

				// Fade the narrower highlights in and out subtly so the animation
				// breathes without drawing attention away from the page content.
				float pulse = 0.92 + (sin((u_time * 0.04) + (y * 2.0)) * 0.08);

				// Preserve the original 90-second colour sequence: hold each colour
				// for 10 seconds, transition for 5, then reverse back to the start.
				float timeInCycle = mod(u_time, 90.0);
				float phaseIndex = floor(timeInCycle / 15.0);
				float timeInPhase = mod(timeInCycle, 15.0);
				float colourProgress = smoothstep(10.0, 15.0, timeInPhase);
				float fromIndex = phaseIndex <= 3.0
					? phaseIndex
					: 6.0 - phaseIndex;
				float toIndex = phaseIndex < 3.0
					? phaseIndex + 1.0
					: 5.0 - phaseIndex;
				vec3 activeAuroraColour = mix(
					getAuroraColour(fromIndex),
					getAuroraColour(toIndex),
					colourProgress
				);
				vec3 adjacentAuroraColour = getAuroraColour(toIndex);

				// Keep the FC blue clearly present rather than darkening the whole
				// canvas. Each curtain can then show its colour at useful contrast.
				vec3 finalColor = mix(u_colorBlue, activeAuroraColour, haze * 0.16);
				finalColor = mix(finalColor, activeAuroraColour, violetCurtain * 0.78);
				finalColor = mix(
					finalColor,
					adjacentAuroraColour,
					magentaCurtain * 0.68 * pulse
				);
				finalColor = mix(
					finalColor,
					activeAuroraColour,
					innerGlow * 0.42 * pulse
				);

				gl_FragColor = vec4(finalColor, 1.0);
				#include <colorspace_fragment>
			}
		`,
	});

	const mesh = new THREE.Mesh(geometry, material);
	scene.add(mesh);

	let animationFrameId = null;
	const clock = new THREE.Clock();
	const reducedMotionQuery = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	);
	let isVisible = false;

	function resize() {
		const width = container.clientWidth;
		const height = container.clientHeight;
		renderer.setSize(width, height, false);
		material.uniforms.u_aspect.value = width / height;
	}

	function animate() {
		if (!isVisible || document.hidden || reducedMotionQuery.matches) return;
		animationFrameId = requestAnimationFrame(animate);
		material.uniforms.u_time.value = clock.getElapsedTime();
		renderer.render(scene, camera);
	}

	function startAnimation() {
		if (reducedMotionQuery.matches) {
			renderer.render(scene, camera);
			return;
		}

		if (!animationFrameId) {
			clock.start();
			animate();
		}
	}

	function stopAnimation() {
		cancelAnimationFrame(animationFrameId);
		animationFrameId = null;
		clock.stop();
	}

	const resizeObserver = new ResizeObserver(() => {
		resize();
		if (!animationFrameId && isVisible) {
			renderer.render(scene, camera);
		}
	});
	resizeObserver.observe(container);

	const intersectionObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				isVisible = entry.isIntersecting;
				if (isVisible && !document.hidden) {
					startAnimation();
				} else {
					stopAnimation();
				}
			});
		},
		{
			threshold: 0.01,
		}
	);

	const observeTarget =
		typeof target.matches === 'function' && target.matches(BLOCK_SELECTOR)
			? target
			: document.body || container;

	if (observeTarget) {
		intersectionObserver.observe(observeTarget);
	}

	document.addEventListener('visibilitychange', () => {
		if (document.hidden) {
			stopAnimation();
		} else if (isVisible) {
			startAnimation();
		}
	});

	reducedMotionQuery.addEventListener('change', () => {
		stopAnimation();
		if (isVisible && !document.hidden) {
			startAnimation();
		}
	});

	resize();
	renderer.render(scene, camera);
}

function initMobileTocDropdown(block) {
	const dropdown = block.querySelector('[data-kh-mobile-toc]');
	const toggle = dropdown?.querySelector('[data-kh-mobile-toc-toggle]');
	const panel = dropdown?.querySelector('[data-kh-mobile-toc-panel]');

	if (!dropdown || !toggle || !panel) {
		return;
	}

	const updatePanelMaxHeight = () => {
		if (toggle.getAttribute('aria-expanded') !== 'true') {
			return;
		}

		const availableHeight = Math.max(
			0,
			window.innerHeight - dropdown.getBoundingClientRect().bottom - 12
		);
		panel.style.setProperty(
			'--kh-mobile-toc-available-height',
			`${availableHeight}px`
		);
	};

	const setExpanded = (isExpanded, returnFocus = false) => {
		toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
		panel.hidden = !isExpanded;
		dropdown.classList.toggle('is-open', isExpanded);

		if (isExpanded) {
			updatePanelMaxHeight();
		}

		if (returnFocus) {
			toggle.focus();
		}
	};

	toggle.addEventListener('click', () => {
		setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
	});

	panel.addEventListener('click', (event) => {
		if (event.target.closest('a[data-target-id]')) {
			setExpanded(false);
		}
	});

	document.addEventListener('click', (event) => {
		if (!dropdown.contains(event.target)) {
			setExpanded(false);
		}
	});

	dropdown.addEventListener('keydown', (event) => {
		if (
			event.key === 'Escape' &&
			toggle.getAttribute('aria-expanded') === 'true'
		) {
			event.preventDefault();
			setExpanded(false, true);
		}
	});

	window.addEventListener('scroll', updatePanelMaxHeight, { passive: true });
	window.addEventListener('resize', updatePanelMaxHeight);
}

function initBlock(block) {
	initH2ContentToc(block, {
		contentSelector: '[data-kh-content]',
		tocWrapperSelector: '[data-kh-toc]',
		tocListSelector: '[data-kh-toc-list]',
		mirroredTocWrapperSelector: '[data-kh-mobile-toc]',
		mirroredTocListSelector: '[data-kh-mobile-toc-list]',
		tocViewportSelector: '[data-kh-toc-window-viewport]',
		tocWindowSelector: '[data-kh-toc-window-list]',
		toggleSelector: '[data-kh-toc-toggle]',
		toggleWrapSelector: '[data-kh-toc-toggle-wrap]',
		linkClassName: CONTENT_TOC_LINK_CLASSES,
		listItemClassName: CONTENT_TOC_LIST_ITEM_CLASSES,
		mirroredLinkClassName: MOBILE_TOC_LINK_CLASSES,
		mirroredListItemClassName: MOBILE_TOC_LIST_ITEM_CLASSES,
		activeClass: ACTIVE_CLASS,
		defaultVisibleItems: DEFAULT_VISIBLE_ITEMS,
		responsiveVisibleItems: true,
		viewportHeightRatio: 0.45,
		minimumViewportHeight: 220,
		ignoredAncestorClasses: IGNORED_HEADING_ANCESTOR_CLASSES,
		tocListDisplay: 'grid',
		toggleDisplay: 'inline-flex',
		viewportDisplay: 'block',
		showMoreLabel: 'See all sections +',
		showLessLabel: 'Show fewer sections -',
		onItemsChange: (items) => syncSectionCount(block, items.length),
	});
	initMobileTocDropdown(block);
	initThreeGradient(block);
}

function initKnowledgeHubContainers() {
	document
		.querySelectorAll('.fc-animated-gradient-bg')
		.forEach((container) => {
			initThreeGradient(container);
		});
	document.querySelectorAll(BLOCK_SELECTOR).forEach(initBlock);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initKnowledgeHubContainers);
} else {
	initKnowledgeHubContainers();
}
